import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetAfeLinesQuery } from "@api/pApi";
import { fmtMoney, fmtSignedMoney } from "@lib/format";
import type { AfeLineOut } from "@api/types";

/** The account label for a line — cost class + code when present, else the ref. */
function accountLabel(line: AfeLineOut): string {
  const parts = [line.cost_class_entry_key, line.cost_code_entry_key].filter(Boolean);
  return parts.length ? parts.join(" · ") : line.afe_ref;
}

/**
 * AFE line grid (§11) — the AFE Management chart-of-accounts grid for an event:
 * each line is an account × program-step budget with an accrual method +
 * accrued (devengado) + invoiced + a live plan-vs-actual variance. Every number
 * is server-computed by Oxbo-P (/afe-lines); the FE only formats and lays out.
 * Money is shown in the grid's reported `currency` (labels the amounts, no
 * implicit USD); a "MIXED" grid falls back to USD-style formatting per amount.
 */
export function AfeGridPanel({ eventId }: { eventId: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetAfeLinesQuery(eventId, {
    skip: !eventId,
  });

  const lines = data?.lines ?? [];
  // A single-currency grid labels every amount; MIXED / null → format without a
  // forced currency (fmtMoney defaults to USD-style grouping) rather than lie.
  const currency = data && data.currency && data.currency !== "MIXED" ? data.currency : undefined;
  const money = (n: number | null | undefined) => fmtMoney(n, locale, { currency });

  const stats: Stat[] = data
    ? [
        {
          label: t("afe.kpi.afeTotal"),
          value: fmtMoney(data.afe_total, locale, { compact: true, currency }),
        },
        {
          label: t("afe.kpi.accrued"),
          value: fmtMoney(data.accrued_total, locale, { compact: true, currency }),
          tone: "ops",
        },
        {
          label: t("cost.kpi.actual"),
          value: fmtMoney(data.actual_total, locale, { compact: true, currency }),
          tone: "ops",
        },
        {
          label: t("afe.kpi.invoiced"),
          value: fmtMoney(data.invoiced_total, locale, { compact: true, currency }),
        },
        {
          label: t("cost.kpi.variance"),
          value: fmtSignedMoney(data.variance, locale, currency ?? "USD"),
          tone: data.variance > 0 ? "crit" : "good",
        },
      ]
    : [];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && lines.length === 0}
      emptyMessage={t("afe.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("afe.grid.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("afe.grid.title")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">{t("afe.col.seq")}</th>
                <th>{t("afe.col.account")}</th>
                <th>{t("afe.col.phase")}</th>
                <th>{t("afe.col.vendor")}</th>
                <th>{t("afe.col.description")}</th>
                <th>{t("afe.col.method")}</th>
                <th className="num">{t("afe.col.budget")}</th>
                <th className="num">{t("afe.col.accrued")}</th>
                <th className="num">{t("afe.col.invoiced")}</th>
                <th className="num">{t("afe.col.actual")}</th>
                <th className="num">{t("afe.col.variance")}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const over = line.variance > 0;
                return (
                  <tr key={line.id} data-owner="p">
                    <td className="num tab-nums">{line.sequence_no ?? line.program_step ?? "—"}</td>
                    <td>{accountLabel(line)}</td>
                    <td>{line.phase_entry_key ?? "—"}</td>
                    <td>{line.vendor_entry_key ?? "—"}</td>
                    <td style={{ color: "var(--dim)", maxWidth: 260 }}>
                      {line.item_description ?? "—"}
                    </td>
                    <td>
                      <span className="mono" style={{ fontSize: 12 }}>
                        {line.accrual_method ?? line.treatment}
                      </span>
                    </td>
                    <td className="num tab-nums">{money(line.afe_amount)}</td>
                    <td className="num tab-nums">{money(line.accrued)}</td>
                    <td className="num tab-nums">{money(line.invoiced)}</td>
                    <td className="num tab-nums">{money(line.actual)}</td>
                    <td className={`num tab-nums ${over ? "var-bad" : "var-good"}`}>
                      {fmtSignedMoney(line.variance, locale, currency ?? "USD")}
                    </td>
                  </tr>
                );
              })}
              {data ? (
                <tr className="total-row" data-owner="p">
                  <td className="num" />
                  <td>{t("cost.total")}</td>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td className="num tab-nums">{money(data.afe_total)}</td>
                  <td className="num tab-nums">{money(data.accrued_total)}</td>
                  <td className="num tab-nums">{money(data.invoiced_total)}</td>
                  <td className="num tab-nums">{money(data.actual_total)}</td>
                  <td className={`num tab-nums ${data.variance > 0 ? "var-bad" : "var-good"}`}>
                    {fmtSignedMoney(data.variance, locale, currency ?? "USD")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}
